import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { listRow, MenuOption } from '@standard-angular-utilities/shared-components';
import { forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import {
  EquipmentSummaryDTO,
  SubCategorySummaryDTO,
  LocationSummaryDTO,
  EquipmentDetailDTO,
  CategorySummaryDTO,
  IsServiceOkDTO,
  EquipmentBorrowSummaryDTO,
} from 'projects/EquipmentRegistration/src/equipment-registration-base/dataTypes';
import {
  BaseListPageComponent,
  checkbox,
  multiSelectFilterList,
} from 'src/app/BasePages/base-list-page/base-list-page.component';
import { DepartmentDTO, UserDTO } from 'BaseDataTypes';
import { MainService } from 'BaseServices/main-service';
import { UserService } from 'BaseServices/user-service';
import { DepartmentService } from 'TrainingTrackingServices/department-service';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-equipment-listview',
  imports: [BaseListPageComponent, FormsModule, MatCheckboxModule, MatButtonModule],
  templateUrl: './equipment-listview.component.html',
  styleUrl: './equipment-listview.component.css',
})
export class EquipmentListviewComponent implements OnInit, AfterViewInit {
  categories: CategorySummaryDTO[] = [];
  subCategories: SubCategorySummaryDTO[] = [];
  locations: LocationSummaryDTO[] = [];
  departments: DepartmentDTO[] = [];
  responsibleUsers: UserDTO[] = [];
  equipmentBorrows: EquipmentBorrowSummaryDTO[] = [];
  checkboxes: checkbox[] = [];

  headerList: string[] = [
    'Id',
    'Navn',
    'Under kategori',
    'Qr url',
    'Model',
    'Afdeling',
    'Sidst kontrolleret',
    'Næste kontrol',
    'Kontrol status',
    'Placering',
    'Ansvarlig',
    'Projekt nr.',
    'Kommentar',
  ];

  @ViewChild('baseListPage')
  baseListPage!: BaseListPageComponent<EquipmentSummaryDTO>;
  extraBurgerOptions: MenuOption[] = [
    ['Print viste liste med qr koder som mdb fil', () => this.printAccessMdb()],
    ['Print PDF med Qr koder', () => this.printPdfWithQrCodes()]
  ];

  readonly isServiceOkLabel: Record<number, string> = {
    [IsServiceOkDTO.OK]: 'OK',
    [IsServiceOkDTO.IkkeKontrolleret]: 'Ikke kontrolleret',
    [IsServiceOkDTO.Defekt]: 'Defekt',
  };

  multiSelectFilterLists: multiSelectFilterList[] = [];
  equipments: EquipmentSummaryDTO[] = [];

  showOnlyBorrowed = false;

  private parseNumberOrDefaultZero(value: unknown): number {
    if (value === null || value === undefined) return 0;
    if (value === '') return 0;

    const parsedNumber = Number(value);
    return Number.isFinite(parsedNumber) ? parsedNumber : 0;
  }

  private getFirstValidTextValue(...values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;

      const trimmedText = String(value).trim();
      if (
        trimmedText !== '' &&
        trimmedText !== 'null' &&
        trimmedText !== 'undefined'
      ) {
        return trimmedText;
      }
    }
    return '';
  }

  private formatDate(value?: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    if (date.getFullYear() <= 1901) return '';
    return date.toLocaleDateString('da-DK');
  }

  private isBorrowed(equipmentId: number): boolean {
    return (this.equipmentBorrows ?? []).some((borrow: any) => {
      const borrowedEquipmentId =
        this.parseNumberOrDefaultZero(borrow.EquipmentId ?? borrow.equipmentId) 

      if (borrowedEquipmentId !== equipmentId) return false;

      const returned = borrow.ReturnedDate ?? borrow.returnedDate;
      return !returned;
    });
  }

  private addMonths(date: Date, months: number): Date {
    const nextControlDate = new Date(date);
    const day = nextControlDate.getDate();
    nextControlDate.setMonth(nextControlDate.getMonth() + months);
    if (nextControlDate.getDate() < day) nextControlDate.setDate(0);
    return nextControlDate;
  }

  private parseFrequencyMonths(frequencyText: any): number {
    const text = String(frequencyText ?? '').trim();
    if (!text) return 0;
    const match = text.match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  private calcNextControlDate(lastDate: any, frequencyText: any): Date | null {
    if (!lastDate) return null;

    const parsed = Date.parse(lastDate);
    if (!parsed) return null;

    const date = new Date(parsed);

    const months = this.parseFrequencyMonths(frequencyText);
    if (months <= 0) return null;

    return this.addMonths(date, months);
  }

  constructor(
    private mainService: MainService,
    private userService: UserService,
    private departmentService: DepartmentService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient
  ) { }

  ngOnInit(): void { }

  checkboxUpdate = (checked: boolean) => {
    this.showOnlyBorrowed = checked;
    this.updateListFromEquipments();
  };

  ngAfterViewInit(): void {
    this.updateListEquipments();
  }

  private updateListEquipments(): void {
    forkJoin({
      equipments: this.mainService.getAll<EquipmentSummaryDTO>('Equipment'),
      categories: this.mainService.getAll<CategorySummaryDTO>('Category'),
      subCategories: this.mainService.getAll<SubCategorySummaryDTO>('SubCategory'),
      locations: this.mainService.getAll<LocationSummaryDTO>('Location'),
      equipmentBorrows: this.mainService.getAll<EquipmentBorrowSummaryDTO>('Equipmentborrows'),
      departments: this.departmentService.getDepartments(),
      responsibleUsers: this.userService.getUserDTOs(),
    }).subscribe({
      next: ({
        equipments,
        categories,
        subCategories,
        locations,
        equipmentBorrows,
        departments,
        responsibleUsers,
      }) => {
        this.equipments = equipments ?? [];
        this.categories = categories ?? [];
        this.subCategories = subCategories ?? [];
        this.locations = locations ?? [];
        this.equipmentBorrows = equipmentBorrows ?? [];
        this.departments = departments ?? [];
        this.responsibleUsers = responsibleUsers ?? [];

        this.hydrateEquipmentsFromLookups();
        this.updateMultiSelectFilterLists();
        this.updateListFromEquipments();

        this.checkboxes = [
          {
            binding: this.showOnlyBorrowed,
            onchanged: this.checkboxUpdate,
            text: 'Vis kun udlånt udstyr',
          },
        ];
      },
    });
  }

  private hydrateEquipmentsFromLookups(): void {
    if (!this.equipments?.length) return;

    const projectNrByEquipmentId = new Map<number, string>();
    (this.equipmentBorrows ?? []).forEach((borrow: any) => {
      const equipmentId =
        this.parseNumberOrDefaultZero(borrow.EquipmentId ?? borrow.equipmentId) ||
        this.parseNumberOrDefaultZero(borrow.Equipment?.Id ?? borrow.Equipment?.id);

      const projectNr = this.getFirstValidTextValue(borrow.ProjectNr, borrow.projectNr);

      if (equipmentId && projectNr && !projectNrByEquipmentId.has(equipmentId)) {
        projectNrByEquipmentId.set(equipmentId, projectNr);
      }
    });

    this.equipments = this.equipments.map((equipment: any) => {
      const equipmentId = this.parseNumberOrDefaultZero(equipment.Id ?? equipment.id);

      const departmentId = this.parseNumberOrDefaultZero(
        equipment.DepartmentId ?? equipment.departmentId ?? equipment.Department?.id ?? equipment.Department?.Id
      );

      const locationId = this.parseNumberOrDefaultZero(
        equipment.LocationId ?? equipment.locationId ?? equipment.Location?.Id ?? equipment.Location?.id
      );

      const subCategoryId = this.parseNumberOrDefaultZero(
        equipment.SubCategoryId ?? equipment.subCategoryId ?? equipment.SubCategory?.Id ?? equipment.SubCategory?.id
      );

      const responsibleUserId = this.parseNumberOrDefaultZero(
        equipment.ResponsibleUserId ??
        equipment.responsibleUserId ??
        equipment.ResponsibleId ??
        equipment.responsibleId ??
        equipment.Responsible?.id ??
        equipment.Responsible?.Id
      );

      const department =
        this.departments.find(
          (d) => this.parseNumberOrDefaultZero((d as any).id ?? (d as any).Id) === departmentId
        ) ?? equipment.Department;

      const location =
        this.locations.find(
          (l) => this.parseNumberOrDefaultZero((l as any).Id ?? (l as any).id) === locationId
        ) ?? equipment.Location;

      const subCategory =
        this.subCategories.find(
          (sc) => this.parseNumberOrDefaultZero((sc as any).Id ?? (sc as any).id) === subCategoryId
        ) ?? equipment.Subcategory;

      const responsibleUser =
        this.responsibleUsers.find(
          (u) => this.parseNumberOrDefaultZero((u as any).id ?? (u as any).Id) === responsibleUserId
        ) ?? equipment.Responsible;

      const projectNr = projectNrByEquipmentId.get(equipmentId) ?? '';

      return {
        ...equipment,
        Department: department,
        Location: location,
        SubCategory: subCategory,
        Responsible: responsibleUser,
        ProjectNr: projectNr
      };
    });
  }

  private updateMultiSelectFilterLists(): void {
    const comp = this;
    const list: multiSelectFilterList[] = [];

    const subToCategoryId = new Map<number, number>();
    (this.subCategories ?? []).forEach((subCategory: any) => {
      const subCategoryId = comp.parseNumberOrDefaultZero(subCategory.Id ?? subCategory.id);
      const categoryId = comp.parseNumberOrDefaultZero(subCategory.CategoryId ?? subCategory.categoryId);
      if (subCategoryId) subToCategoryId.set(subCategoryId, categoryId);
    });

    const categoryFilter: multiSelectFilterList = {
      Items: (this.categories ?? []).map((category: any) => ({
        id: comp.parseNumberOrDefaultZero(category.id ?? category.Id),
        name: comp.getFirstValidTextValue(category.Name, category.name),
      })),
      selectedItemsIds: (this.categories ?? []).map((category: any) =>
        comp.parseNumberOrDefaultZero(category.id ?? category.Id)
      ),
      placeHolderText: 'Vælg kategori',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const subCategoryId = comp.parseNumberOrDefaultZero(
          equipment?.SubCategory?.Id ??
          equipment?.SubCategory?.id ??
          equipment?.Subcategory?.Id ??
          equipment?.Subcategory?.id ??
          equipment?.SubCategoryId ??
          equipment?.subCategoryId ??
          equipment?.SubcategoryId ??
          equipment?.subcategoryId
        );

        const categoryId = subToCategoryId.get(subCategoryId) ?? 0;
        return !subCategoryId || this.selectedItemsIds.includes(categoryId);
      },
      HideWithHeaderIndex: 2,
    };
    list.push(categoryFilter);

    const subCategoryFilter: multiSelectFilterList = {
      Items: (this.subCategories ?? []).map((subCategory: any) => ({
        id: comp.parseNumberOrDefaultZero(subCategory.Id ?? subCategory.id),
        name: comp.getFirstValidTextValue(subCategory.Name, subCategory.name),
      })),
      selectedItemsIds: (this.subCategories ?? []).map((subCategory: any) =>
        comp.parseNumberOrDefaultZero(subCategory.Id ?? subCategory.id)
      ),
      placeHolderText: 'Vælg underkategori',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const subCategoryId = comp.parseNumberOrDefaultZero(
          equipment?.SubCategory?.Id ??
          equipment?.SubCategory?.id ??
          equipment?.subCategoryId ??
          equipment?.SubcategoryId ??
          equipment?.subcategoryId
        );

        return !subCategoryId || this.selectedItemsIds.includes(subCategoryId);
      },
      HideWithHeaderIndex: 3,
    };
    list.push(subCategoryFilter);

    const departmentFilter: multiSelectFilterList = {
      Items: (this.departments ?? []).map((department: any) => ({
        id: comp.parseNumberOrDefaultZero(department.id ?? department.Id),
        name: comp.getFirstValidTextValue(
          department.displayName,
          department.departmentName,
          department.Name,
          department.name
        ),
      })),
      selectedItemsIds: (this.departments ?? []).map((department: any) =>
        comp.parseNumberOrDefaultZero(department.id ?? department.Id)
      ),
      placeHolderText: 'Vælg afdeling',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const departmentId = comp.parseNumberOrDefaultZero(
          equipment?.Department?.Id ??
          equipment?.Department?.id ??
          equipment?.DepartmentId ??
          equipment?.departmentId
        );
        return !departmentId || this.selectedItemsIds.includes(departmentId);
      },
      HideWithHeaderIndex: 5,
    };
    list.push(departmentFilter);

    const statusFilter: multiSelectFilterList = {
      Items: [
        { id: IsServiceOkDTO.OK, name: 'OK' },
        { id: IsServiceOkDTO.IkkeKontrolleret, name: 'Ikke kontrolleret' },
        { id: IsServiceOkDTO.Defekt, name: 'Defekt' },
      ],
      selectedItemsIds: [IsServiceOkDTO.OK, IsServiceOkDTO.IkkeKontrolleret, IsServiceOkDTO.Defekt],
      placeHolderText: 'Vælg kontrol status',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const serviceOkValue =
          equipment?.IsServiceOK ?? equipment?.isServiceOK ?? equipment?.IsServiceOk ?? equipment?.isServiceOk;
        return this.selectedItemsIds.includes(comp.parseNumberOrDefaultZero(serviceOkValue));
      },
      HideWithHeaderIndex: 8,
    };
    list.push(statusFilter);

    const placements = (this.equipments ?? [])
      .map((equipment: any) => comp.getFirstValidTextValue(equipment.Placement, equipment.placement))
      .filter((placement: string) => !!placement)
      .filter((placement: string, index: number, arr: string[]) => arr.indexOf(placement) === index)
      .sort((a: string, b: string) => a.localeCompare(b, 'da-DK'));

    const placementFilter: multiSelectFilterList = {
      Items: placements.map((placement) => ({ id: placement as any, name: placement })),
      selectedItemsIds: placements.map((placement) => placement as any),
      placeHolderText: 'Vælg placering',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const selected = this.selectedItemsIds as any[] | undefined;

        if (!selected || selected.length === 0) return false;

        const placement = comp.getFirstValidTextValue(equipment?.Placement, equipment?.placement);

        return placement !== '' && selected.includes(placement as any);
      },
      HideWithHeaderIndex: 9,
    };
    list.push(placementFilter);

    const responsibleFilter: multiSelectFilterList = {
      Items: (this.responsibleUsers ?? []).map((user: any) => ({
        id: comp.parseNumberOrDefaultZero(user.id ?? user.Id),
        name: comp.getFirstValidTextValue(user.name, user.Name),
      })),
      selectedItemsIds: (this.responsibleUsers ?? []).map((user: any) =>
        comp.parseNumberOrDefaultZero(user.id ?? user.Id)
      ),
      placeHolderText: 'Vælg ansvarlig',
      selectedValueChange: function (event) {
        this.selectedItemsIds = event;
      },
      filterFunction: function (equipment: any) {
        const responsibleId = comp.parseNumberOrDefaultZero(
          equipment?.Responsible?.Id ??
          equipment?.Responsible?.id ??
          equipment?.ResponsibleUserId ??
          equipment?.responsibleUserId
        );
        return !responsibleId || this.selectedItemsIds.includes(responsibleId);
      },
      HideWithHeaderIndex: 9,
    };
    list.push(responsibleFilter);

    this.multiSelectFilterLists = list;
  }

  public updateListFromEquipments = () => {
    if (!this.baseListPage) return;

    const categoryNameById = new Map<number, string>();
    (this.categories ?? []).forEach((category: any) => {
      const categoryId = this.parseNumberOrDefaultZero(category.Id ?? category.id);
      if (!categoryId) return;
      categoryNameById.set(categoryId, this.getFirstValidTextValue(category.Name, category.name));
    });

    const subById = new Map<number, { name: string; categoryId: number }>();
    (this.subCategories ?? []).forEach((subCategory: any) => {
      const subCategoryId = this.parseNumberOrDefaultZero(subCategory.Id ?? subCategory.id);
      if (!subCategoryId) return;

      const subCategoryName = this.getFirstValidTextValue(subCategory.Name, subCategory.name);
      const categoryId = this.parseNumberOrDefaultZero(subCategory.CategoryId ?? subCategory.categoryId);

      subById.set(subCategoryId, { name: subCategoryName, categoryId });
    });

    const userNameById = new Map<number, string>();
    (this.responsibleUsers ?? []).forEach((user: any) => {
      const userId = this.parseNumberOrDefaultZero(user.Id ?? user.id);
      if (!userId) return;
      userNameById.set(userId, this.getFirstValidTextValue(user.name, user.Name));
    });

    const projectNrByEquipmentId = new Map<number, string>();
    (this.equipmentBorrows ?? []).forEach((borrow: any) => {
      const equipmentId =
        this.parseNumberOrDefaultZero(borrow.EquipmentId ?? borrow.equipmentId) ||
        this.parseNumberOrDefaultZero(borrow.Equipment?.Id ?? borrow.Equipment?.id);

      const projectNr = this.getFirstValidTextValue(borrow.ProjectNr, borrow.projectNr);

      if (equipmentId && projectNr && !projectNrByEquipmentId.has(equipmentId)) {
        projectNrByEquipmentId.set(equipmentId, projectNr);
      }
    });

    const equipmentIdFromEquipment = (equipment: any) => this.parseNumberOrDefaultZero(equipment.Id ?? equipment.id);

    const subCategoryIdFromEquipment = (equipment: any) => this.parseNumberOrDefaultZero(equipment.subCategoryId ?? equipment.subcategoryId);

    const responsibleUserIdFromEquipment = (equipment: any) => this.parseNumberOrDefaultZero(equipment.responsibleId);

    const listData: listRow<EquipmentSummaryDTO>[] = [];

    (this.equipments ?? []).forEach((equipment: any) => {
      const equipmentId = equipmentIdFromEquipment(equipment);

      if (this.showOnlyBorrowed && !this.isBorrowed(equipmentId)) return;

      const name = this.getFirstValidTextValue(equipment.name);
      const model = this.getFirstValidTextValue(equipment.model);
      const placement = this.getFirstValidTextValue(equipment.placement);
      const comment = this.getFirstValidTextValue(equipment.comment, equipment.usageNotice);
      const serviceOkVal = equipment.IsServiceOK;
      const serviceOk = this.isServiceOkLabel[this.parseNumberOrDefaultZero(serviceOkVal)] ?? (serviceOkVal == null ? '' : String(serviceOkVal));
      const qrUrl = 'apps.kruger.dk/udstyr/kontrollerudstyr/' + equipmentId
      const subCategoryId = subCategoryIdFromEquipment(equipment);
      const subInfo = subById.get(subCategoryId);
      const subCategoryName = subInfo?.name ?? '';
      const supplierName = this.getFirstValidTextValue(equipment.supplierName);
      const dept = equipment.Department ?? equipment.department;
      const departmentName = this.getFirstValidTextValue(dept?.departmentName);
      const lastServiceRaw = equipment.LastServiceDate ?? (equipment as any).lastServiceDate;
      const lastService = this.formatDate(lastServiceRaw);

      let nextService = '';
        if (lastService) {
          const freqText = equipment.serviceFrequencyMonths;
          const nextDate = this.calcNextControlDate(lastServiceRaw, freqText);
          nextService = nextDate ? nextDate.toLocaleDateString('da-DK') : '';
        }

      const responsibleId = responsibleUserIdFromEquipment(equipment);
      const responsibleName = userNameById.get(responsibleId) ?? '';
      const projectNr = this.getFirstValidTextValue(
        projectNrByEquipmentId.get(equipmentId),
        (equipment as any).ProjectNr,
        (equipment as any).projectNr
      );

      listData.push({
        object: equipment,
        id: equipmentId,
        data: [
          String(equipmentId),
          name,
          subCategoryName,
          qrUrl,
          model,
          departmentName,
          lastService,
          nextService,
          serviceOk,
          placement,
          responsibleName,
          projectNr,
          comment,
        ],
      });
    });

    this.baseListPage.UpdateListData(listData);
  };

  private getVisibleEquipmentIds(): number[] {
    const rows = this.baseListPage?.listView?.presentedList() ?? [];

    return rows
      .map((row: any) => Number(row.id))
      .filter((id: number) => Number.isFinite(id) && id > 0);
  }

  printAccessMdb(): void {
    const equipmentIds = this.getVisibleEquipmentIds();

    if (!equipmentIds.length) {
      console.error('Ingen viste rækker at eksportere');
      return;
    }

    this.http.post(
      '/api/EquipmentExport/access-mdb-by-ids',
      { equipmentIds },
      { responseType: 'blob' }
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'QRCodes.mdb';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: async (error: any) => {
        console.error('Fejl ved eksport til MDB med QR koder', error);

        if (error?.error instanceof Blob) {
          const text = await error.error.text();
          console.error('Backend fejltekst:', text);
        }
      }
    });
  }

  printPdfWithQrCodes(): void {
    const equipmentIds = this.getVisibleEquipmentIds();

    if (!equipmentIds.length) {
      console.error('Ingen viste rækker at eksportere');
      return;
    }

    this.http.post(
      '/api/EquipmentExport/pdf-qr-by-ids',
      { equipmentIds },
      { responseType: 'blob' }
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'QRCodes.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: async (error: any) => {
        console.error('Fejl ved eksport til PDF med QR koder', error);

        if (error?.error instanceof Blob) {
          const text = await error.error.text();
          console.error('Backend fejltekst:', text);
        }
      }
    });
  }

  equipmentClicked = (equipment?: EquipmentSummaryDTO) => {
    if (!equipment) return;

    const eq = equipment as any;

    const department = eq.Department ?? {};
    const location = eq.Location ?? {};
    const subCategory = eq.SubCategory ?? {};

    const equipmentRequest: EquipmentDetailDTO = {
      id: this.parseNumberOrDefaultZero(eq.id ?? eq.Id),
      name: eq.name ?? '',
      model: eq.model ?? '',
      series: eq.series ?? '',
      comment: this.getFirstValidTextValue(eq.comment, eq.usageNotice),
      qrToken: eq.QrToken ?? '',
      supplierName: eq.SupplierName ?? '',
      serviceFrequencyMonths: eq.ServiceFrequency ?? '',
      serviceFrequencyDelay: this.parseNumberOrDefaultZero(eq.ServiceFrequencyDelay),
      purchaseDate: eq.purchaseDate ?? new Date(),
      placement: eq.Placement ?? '',
      alarmRecipientEmail: eq.AlarmRecipient ?? '',
      supplierPhone: eq.SupplierPhone ?? '',
      supplierAddress: eq.SupplierAddress ?? '',
      bestbefore: eq.BestBefore ?? new Date(),
      guideLink: eq.GuideLink ?? '',
      responsibleId: this.parseNumberOrDefaultZero(eq.ResponsibleId),
      isServiceOK: eq.isServiceOK ?? IsServiceOkDTO.IkkeKontrolleret,
      departmentId: this.parseNumberOrDefaultZero(department.id ?? department.Id ?? eq.DepartmentId),
      locationId: this.parseNumberOrDefaultZero(location.Id ?? location.id ?? eq.LocationId),
      subCategoryId: this.parseNumberOrDefaultZero(subCategory.Id ?? subCategory.id ?? eq.SubCategoryId),
      hasServiceHistory: eq.HasServiceHistory ?? false,
    };

    const id = Number((equipment as any).Id ?? (equipment as any).id ?? 0);
    if (!id) return;

    this.router.navigate(['../opretudstyr', id], { relativeTo: this.activatedRoute });
  };
}
